import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { useApiMutatie } from './mutaties.ts';
import type {
  Certificaattype,
  CertificaattypeInvoer,
  Functietitel,
  FunctietitelInvoer,
  Locatie,
  LocatieInvoer,
  Talentpool,
  TalentpoolInvoer,
  Vaardigheid,
  VaardigheidInvoer,
} from './typen.ts';

export type Referentiesoort = 'talentpools' | 'functietitels' | 'vaardigheden' | 'certificaattypes';

export interface Referentielijst<T, I> {
  soort: Referentiesoort;
  enkelvoud: string;
  meervoud: string;
  zoek(term: string, limiet?: number): Promise<T[]>;
  maak(invoer: I): Promise<T>;
  wijzig(id: string, invoer: Partial<I>): Promise<T>;
  verwijder(id: string): Promise<void>;
}

const standaardLimiet = 500;

export const talentpoolLijst: Referentielijst<Talentpool, TalentpoolInvoer> = {
  soort: 'talentpools',
  enkelvoud: 'talentpool',
  meervoud: 'talentpools',
  zoek: (zoek, limiet = standaardLimiet) =>
    uitvoeren(api.GET('/api/v1/talentpools', { params: { query: zoek ? { zoek, limiet } : { limiet } } })),
  maak: (body) => uitvoeren(api.POST('/api/v1/talentpools', { body })),
  wijzig: (id, body) => uitvoeren(api.PATCH('/api/v1/talentpools/{id}', { params: { path: { id } }, body })),
  verwijder: (id) => uitvoeren(api.DELETE('/api/v1/talentpools/{id}', { params: { path: { id } } })),
};

export const functietitelLijst: Referentielijst<Functietitel, FunctietitelInvoer> = {
  soort: 'functietitels',
  enkelvoud: 'functietitel',
  meervoud: 'functietitels',
  zoek: (zoek, limiet = standaardLimiet) =>
    uitvoeren(api.GET('/api/v1/functietitels', { params: { query: zoek ? { zoek, limiet } : { limiet } } })),
  maak: (body) => uitvoeren(api.POST('/api/v1/functietitels', { body })),
  wijzig: (id, body) => uitvoeren(api.PATCH('/api/v1/functietitels/{id}', { params: { path: { id } }, body })),
  verwijder: (id) => uitvoeren(api.DELETE('/api/v1/functietitels/{id}', { params: { path: { id } } })),
};

export const vaardigheidLijst: Referentielijst<Vaardigheid, VaardigheidInvoer> = {
  soort: 'vaardigheden',
  enkelvoud: 'vaardigheid',
  meervoud: 'vaardigheden',
  zoek: (zoek, limiet = standaardLimiet) =>
    uitvoeren(api.GET('/api/v1/vaardigheden', { params: { query: zoek ? { zoek, limiet } : { limiet } } })),
  maak: (body) => uitvoeren(api.POST('/api/v1/vaardigheden', { body })),
  wijzig: (id, body) => uitvoeren(api.PATCH('/api/v1/vaardigheden/{id}', { params: { path: { id } }, body })),
  verwijder: (id) => uitvoeren(api.DELETE('/api/v1/vaardigheden/{id}', { params: { path: { id } } })),
};

export const certificaattypeLijst: Referentielijst<Certificaattype, CertificaattypeInvoer> = {
  soort: 'certificaattypes',
  enkelvoud: 'certificaattype',
  meervoud: 'certificaattypes',
  zoek: (zoek, limiet = standaardLimiet) =>
    uitvoeren(api.GET('/api/v1/certificaattypes', { params: { query: zoek ? { zoek, limiet } : { limiet } } })),
  maak: (body) => uitvoeren(api.POST('/api/v1/certificaattypes', { body })),
  wijzig: (id, body) => uitvoeren(api.PATCH('/api/v1/certificaattypes/{id}', { params: { path: { id } }, body })),
  verwijder: (id) => uitvoeren(api.DELETE('/api/v1/certificaattypes/{id}', { params: { path: { id } } })),
};

export const referentieSleutels = {
  lijst: (soort: Referentiesoort | 'locaties', zoek = '') => ['referentie', soort, zoek] as const,
  alle: (soort: Referentiesoort | 'locaties') => ['referentie', soort] as const,
};

/**
 * Zoekt in een referentielijst, of geeft alles zonder zoekterm.
 * @param lijst De referentielijst.
 * @param zoek De zoekterm.
 * @returns De query met de gevonden records.
 */
export function useReferentielijst<T, I>(lijst: Referentielijst<T, I>, zoek = '') {
  return useQuery({
    queryKey: referentieSleutels.lijst(lijst.soort, zoek),
    queryFn: () => lijst.zoek(zoek),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/**
 * De mutaties op een referentielijst: toevoegen, wijzigen en verwijderen.
 * @param lijst De referentielijst.
 * @returns De drie mutaties.
 */
export function useReferentieMutaties<T, I>(lijst: Referentielijst<T, I>) {
  const sleutels = () => [referentieSleutels.alle(lijst.soort)];
  return {
    maak: useApiMutatie((invoer: I) => lijst.maak(invoer), sleutels),
    wijzig: useApiMutatie(({ id, invoer }: { id: string; invoer: Partial<I> }) => lijst.wijzig(id, invoer), sleutels),
    verwijder: useApiMutatie((id: string) => lijst.verwijder(id), sleutels),
  };
}

/**
 * Voegt een synoniem toe aan een functietitel of vaardigheid.
 * @returns De mutatie.
 */
export function useSynoniemToevoegen() {
  return useApiMutatie(
    ({ soort, id, synoniem }: { soort: 'functietitels' | 'vaardigheden'; id: string; synoniem: string }) =>
      soort === 'functietitels'
        ? uitvoeren(api.POST('/api/v1/functietitels/{id}/synoniemen', { params: { path: { id } }, body: { synoniem } }))
        : uitvoeren(api.POST('/api/v1/vaardigheden/{id}/synoniemen', { params: { path: { id } }, body: { synoniem } })),
    ({ soort }) => [referentieSleutels.alle(soort)],
  );
}

/**
 * Stelt talentpools voor bij een stel functietitels.
 * @param functietitelIds De functietitels.
 * @returns De query met de voorgestelde talentpools.
 */
export function useTalentpoolvoorstel(functietitelIds: readonly string[]) {
  const ids = [...functietitelIds].sort();
  return useQuery({
    queryKey: ['referentie', 'talentpoolvoorstel', ids] as const,
    queryFn: () =>
      uitvoeren(api.GET('/api/v1/functietitels/talentpoolvoorstel', { params: { query: { functietitelId: ids } } })),
    enabled: ids.length > 0,
  });
}

/**
 * Zoekt locaties op (een deel van) de plaatsnaam of op postcode.
 * @param term De zoekterm; een postcode wordt herkend aan vier cijfers gevolgd door twee letters.
 * @returns De gevonden locaties.
 */
export async function zoekLocaties(term: string): Promise<Locatie[]> {
  const schoon = term.trim();
  const isPostcode = /^\d{4}\s?[a-z]{2}$/i.test(schoon);
  return uitvoeren(
    api.GET('/api/v1/locaties', {
      params: { query: isPostcode ? { postcode: schoon } : { plaats: schoon, limiet: 50 } },
    }),
  );
}

/**
 * Zoekt locaties voor een keuzelijst.
 * @param term De zoekterm.
 * @returns De query met de gevonden locaties.
 */
export function useLocaties(term: string) {
  return useQuery({
    queryKey: referentieSleutels.lijst('locaties', term),
    queryFn: () => zoekLocaties(term),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/**
 * Geeft de locatie met een postcode, en maakt hem aan als hij nog niet bestaat.
 * @returns De mutatie.
 */
export function useLocatieVindenOfMaken() {
  return useApiMutatie(
    (invoer: LocatieInvoer) => uitvoeren(api.POST('/api/v1/locaties', { body: invoer })),
    () => [referentieSleutels.alle('locaties')],
  );
}

/**
 * De mutaties op locaties: wijzigen en verwijderen.
 * @returns De mutaties.
 */
export function useLocatieMutaties() {
  const sleutels = () => [referentieSleutels.alle('locaties')];
  return {
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: Partial<LocatieInvoer> }) =>
        uitvoeren(api.PATCH('/api/v1/locaties/{id}', { params: { path: { id } }, body: invoer })),
      sleutels,
    ),
    verwijder: useApiMutatie(
      (id: string) => uitvoeren(api.DELETE('/api/v1/locaties/{id}', { params: { path: { id } } })),
      sleutels,
    ),
  };
}
